import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, BadgeCheck, BookOpen, CheckCircle2, XCircle } from "lucide-react";
import { api } from "@/lib/api";
import { Price } from "@/lib/currency";
import { PERSONAS, waLink } from "@/data/content";
import { Nav } from "@/components/Nav";
import { Seo } from "@/components/Seo";
import { Footer } from "@/components/Footer";
import { FadeUp } from "@/components/Reveal";

const Block = ({ overline, title, children, accent = "border-jawa-gold/40", testid }) => (
  <FadeUp>
    <section className={`border-l-4 ${accent} bg-white p-8 lg:p-10`} data-testid={testid}>
      <p className="text-[10px] uppercase tracking-[0.3em] text-jawa-espresso/50">{overline}</p>
      <h2 className="mt-2 font-serif text-3xl font-medium tracking-tight text-jawa-espresso">{title}</h2>
      <div className="mt-5 text-base leading-relaxed text-jawa-espresso/75">{children}</div>
    </section>
  </FadeUp>
);

export default function JourneyPage() {
  const { journeyId } = useParams();
  const p = PERSONAS[journeyId];
  const [guide, setGuide] = useState(null);

  useEffect(() => {
    api.get("/products").then((r) => {
      const found = r.data.find((prod) => prod.category === "guide" && prod.audience === journeyId && !prod.title.startsWith("Detty December"));
      setGuide(found || null);
    }).catch(() => {});
  }, [journeyId]);

  if (!p) {
    return (
      <div className="min-h-screen bg-jawa-ivory">
        <Nav />
        <div className="mx-auto max-w-3xl px-6 pt-40 pb-24 text-center" data-testid="journey-not-found">
          <h1 className="font-serif text-4xl text-jawa-espresso">Journey not found</h1>
          <Link to="/" className="mt-6 inline-flex items-center gap-2 text-sm uppercase tracking-[0.2em] text-jawa-coral">
            <ArrowLeft className="h-4 w-4" /> Back home
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="bg-jawa-ivory" data-testid={`journey-page-${journeyId}`}>
      <Nav />
      <Seo title={`${p.title} Journey — Meet ${p.name} | JÀWÀ Africa`} description={p.meet} />
      <header className="relative flex min-h-[70vh] items-end overflow-hidden">
        <img src={p.image} alt={p.title} className="absolute inset-0 h-full w-full object-cover object-top" />
        <div className="absolute inset-0 bg-gradient-to-t from-jawa-nearblack/90 via-jawa-nearblack/40 to-jawa-nearblack/25" />
        <div className="relative mx-auto w-full max-w-7xl px-6 pb-16 pt-40 lg:px-10">
          <FadeUp>
            <p className="text-xs uppercase tracking-[0.35em] text-jawa-turquoise">{p.kicker} · {p.title}</p>
            <h1 className="mt-3 font-serif text-5xl font-medium tracking-tight text-jawa-ivory sm:text-6xl">
              Meet {p.name}
            </h1>
            <p className="mt-3 text-sm uppercase tracking-[0.2em] text-jawa-ivory/70">{p.meta}</p>
            <p className="mt-5 max-w-2xl text-base leading-relaxed text-jawa-ivory/85 md:text-lg">{p.meet}</p>
          </FadeUp>
        </div>
      </header>

      <main className="mx-auto max-w-5xl space-y-8 px-6 py-20 lg:px-10 lg:py-24">
        <Block overline="The goal" title="What they're looking for" accent="border-jawa-turquoise" testid="journey-block-looking">
          <p>{p.lookingFor}</p>
        </Block>

        <div className="grid gap-8 md:grid-cols-2">
          <Block overline="Sound familiar?" title="Their frustrations" accent="border-jawa-coral" testid="journey-block-frustrations">
            <p className="font-serif text-xl leading-snug text-jawa-espresso">“{p.frustrations}”</p>
          </Block>
          <Block overline="What's in the way" title="Their barriers" accent="border-jawa-coral" testid="journey-block-barriers">
            <p className="font-serif text-xl leading-snug text-jawa-espresso">“{p.barriers}”</p>
          </Block>
        </div>

        <Block overline="The practical layer" title="What they need" accent="border-jawa-yellow" testid="journey-block-needs">
          <ul className="grid gap-3 sm:grid-cols-2">
            {p.needs.map((n) => (
              <li key={n} className="flex items-start gap-3 text-sm">
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-jawa-turquoise" />
                {n}
              </li>
            ))}
          </ul>
        </Block>

        <Block overline="The JÀWÀ answer" title="How JÀWÀ can help" accent="border-jawa-gold" testid="journey-block-help">
          <p className="font-serif text-xl leading-snug text-jawa-espresso">{p.howHelp}</p>
          <p className="mt-4 text-sm font-semibold uppercase tracking-[0.2em] text-jawa-terracotta">{p.strapline}</p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link
              to="/services"
              data-testid="journey-help-services"
              className="flex items-center gap-1.5 border border-jawa-espresso/25 px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.2em] text-jawa-espresso transition-colors hover:border-jawa-turquoise hover:text-jawa-turquoise"
            >
              JÀWÀ Services <ArrowUpRight className="h-4 w-4" />
            </Link>
            <Link
              to="/guides"
              data-testid="journey-help-guides"
              className="flex items-center gap-1.5 border border-jawa-espresso/25 px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.2em] text-jawa-espresso transition-colors hover:border-jawa-turquoise hover:text-jawa-turquoise"
            >
              Free A–Z resources <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>
        </Block>

        {guide && (
          <FadeUp>
            <section
              className="flex flex-col overflow-hidden border border-jawa-coral/40 bg-white sm:flex-row"
              data-testid="journey-block-guide"
            >
              <div className="relative h-52 overflow-hidden sm:h-auto sm:w-2/5">
                <img src={guide.image_url} alt={guide.title} loading="lazy" className="h-full w-full object-cover" />
                <span className="absolute left-4 top-4 bg-jawa-coral px-3 py-1 text-[10px] font-bold uppercase tracking-[0.2em] text-white">
                  Instant PDF
                </span>
              </div>
              <div className="flex flex-1 flex-col justify-between p-8">
                <div>
                  <p className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-jawa-espresso/50">
                    <BookOpen className="h-3.5 w-3.5" /> Your A–Z guide
                  </p>
                  <h2 className="mt-2 font-serif text-2xl font-medium tracking-tight text-jawa-espresso">{guide.title}</h2>
                  <p className="mt-3 line-clamp-3 text-sm leading-relaxed text-jawa-espresso/65">{guide.description}</p>
                </div>
                <div className="mt-6 flex flex-wrap items-center gap-4 border-t border-jawa-stone pt-5">
                  <span className="font-serif text-3xl font-medium text-jawa-espresso" data-testid="journey-guide-price">
                    <Price amount={guide.price} from={guide.currency || "USD"} />
                  </span>
                  <Link
                    to={`/shop/p/${guide._id}`}
                    data-testid="journey-guide-buy"
                    className="bg-jawa-coral px-6 py-3 text-xs font-semibold uppercase tracking-[0.25em] text-white transition-colors hover:bg-jawa-espresso"
                  >
                    Get the guide
                  </Link>
                </div>
                <p className="mt-3 text-[11px] leading-relaxed text-jawa-espresso/50">
                  The complete flagship PDF — a separate purchase from membership. Members unlock the online A–Z
                  collection instead.
                </p>
              </div>
            </section>
          </FadeUp>
        )}

        <FadeUp>
          <section
            className="relative overflow-hidden bg-jawa-espresso p-8 lg:p-12"
            data-testid="journey-block-membership"
          >
            <div className="absolute -right-16 -top-16 h-48 w-48 rounded-full bg-jawa-coral/20 blur-2xl" />
            <div className="absolute -bottom-16 -left-16 h-48 w-48 rounded-full bg-jawa-turquoise/15 blur-2xl" />
            <p className="text-[10px] uppercase tracking-[0.3em] text-jawa-gold">Explore your membership</p>
            <h2 className="mt-2 font-serif text-3xl font-medium tracking-tight text-jawa-ivory">
              JÀWÀ {p.title.replace("The ", "")} Membership
            </h2>
            <p className="mt-4 max-w-xl text-sm leading-relaxed text-jawa-ivory/70">
              Unlock the members-only {p.guideCta} — a complete A–Z written for {p.title.toLowerCase()}s, with
              practical checklists, directories and resources that stay current.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link
                to={`/membership/${journeyId}`}
                data-testid="journey-membership-cta"
                className="bg-jawa-coral px-7 py-3.5 text-xs font-semibold uppercase tracking-[0.25em] text-white transition-colors hover:bg-jawa-coral/90"
              >
                View Membership
              </Link>
              <a
                href={waLink(`Hello JÀWÀ — I'm like ${p.name} (${p.title}). Can we talk?`)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="journey-whatsapp-cta"
                className="flex items-center gap-1.5 border border-jawa-turquoise px-7 py-3.5 text-xs font-semibold uppercase tracking-[0.25em] text-jawa-turquoise transition-colors hover:bg-jawa-turquoise/10"
              >
                WhatsApp the Concierge <ArrowUpRight className="h-4 w-4" />
              </a>
            </div>
          </section>
        </FadeUp>

        {p.disclaimer && (
          <FadeUp>
            <p className="flex items-start gap-2 text-xs leading-relaxed text-jawa-espresso/50" data-testid="journey-disclaimer">
              <XCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" /> {p.disclaimer}
            </p>
          </FadeUp>
        )}

        <FadeUp>
          <p className="flex items-center gap-2 text-xs text-jawa-espresso/40" data-testid="persona-note">
            <BadgeCheck className="h-3.5 w-3.5" /> Personas are illustrative composites of the people JÀWÀ serves; photographs are representative.
          </p>
        </FadeUp>
      </main>
      <Footer />
    </div>
  );
}
